const SW_PATH = '/image-sw.js';
const SW_READY_EVENT = 'image-sw-ready';
const SW_READY_TIMEOUT = 5000;

let swRegistration: ServiceWorkerRegistration | null = null;
let swReady = false;
let swReadyPromise: Promise<boolean> | null = null;

function isServiceWorkerSupported(): boolean {
  return 'serviceWorker' in navigator && window.isSecureContext;
}

export async function registerImageServiceWorker(): Promise<boolean> {
  if (!isServiceWorkerSupported()) {
    return false;
  }

  if (swReadyPromise) {
    return swReadyPromise;
  }

  swReadyPromise = new Promise<boolean>(async (resolve) => {
    try {
      const existingRegistration = await navigator.serviceWorker.getRegistration(SW_PATH);

      if (existingRegistration?.active) {
        swRegistration = existingRegistration;
        const isActive = await pingServiceWorker();
        if (isActive) {
          swReady = true;
          window.dispatchEvent(new CustomEvent(SW_READY_EVENT));
          resolve(true);
          return;
        }
      }

      swRegistration = await navigator.serviceWorker.register(SW_PATH, {
        scope: '/'
      });

      if (swRegistration.active) {
        swReady = true;
        window.dispatchEvent(new CustomEvent(SW_READY_EVENT));
        resolve(true);
        return;
      }

      const worker = swRegistration.installing || swRegistration.waiting;

      if (worker) {
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated') {
            swReady = true;
            window.dispatchEvent(new CustomEvent(SW_READY_EVENT));
            resolve(true);
          }
        });

        setTimeout(() => {
          if (!swReady) {
            resolve(false);
          }
        }, SW_READY_TIMEOUT);
      } else {
        resolve(false);
      }
    } catch {
      resolve(false);
    }
  });

  return swReadyPromise;
}

async function pingServiceWorker(): Promise<boolean> {
  if (!swRegistration?.active) {
    return false;
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), 1000);
    const channel = new MessageChannel();

    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      resolve(event.data?.type === 'PONG' && event.data?.ready === true);
    };

    swRegistration!.active!.postMessage({ type: 'PING' }, [channel.port2]);
  });
}

export function isServiceWorkerReady(): boolean {
  return swReady;
}

export function waitForServiceWorker(): Promise<boolean> {
  if (swReady) {
    return Promise.resolve(true);
  }

  if (!isServiceWorkerSupported()) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), SW_READY_TIMEOUT);

    window.addEventListener(SW_READY_EVENT, () => {
      clearTimeout(timeout);
      resolve(true);
    }, { once: true });

    if (!swReadyPromise) {
      registerImageServiceWorker();
    }
  });
}

export async function clearServiceWorkerImageCache(): Promise<boolean> {
  if (!swRegistration?.active) {
    return false;
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), 2000);
    const channel = new MessageChannel();

    channel.port1.onmessage = (event) => {
      clearTimeout(timeout);
      resolve(event.data?.success === true);
    };

    swRegistration!.active!.postMessage(
      { type: 'CLEAR_IMAGE_CACHE' },
      [channel.port2]
    );
  });
}

export function getServiceWorkerStatus(): {
  supported: boolean;
  registered: boolean;
  ready: boolean;
} {
  return {
    supported: isServiceWorkerSupported(),
    registered: swRegistration !== null,
    ready: swReady
  };
}
